<template>
  <div
    :class="[
      'workout-block',
      {
        '--show-beep-test': showBeepTest,
        '--invert-colors': invertColors,
        '--timer-shown': isPresent(workoutTimer),
        '--is-resting': activeItemIsRest
      }
    ]"
    :style="{
      padding: !hasGroups || isBeepTest ? 0 : blockPadding
    }"
  >
    <transition name="fade" mode="out-in">
      <div class="workout">
        <template v-if="isBeepTest">
          <template v-if="isBuilderContext()">
            <div class="workout__empty text-muted">
              <v-icon class="mb-2" icon="fitness_center" size="2rem" />
              <div>
                {{ $t("components.blocks.workoutBlock.clickEditWorkout") }}
              </div>
            </div>
          </template>

          <template v-else>
            <template v-if="workoutSession && workoutTimer">
              <workout-session
                :session="workoutSession"
                :session-mode="workoutModePreference"
                :workout-id="workout.id"
                :page-id="pageId"
                :workout-groups="workout.workout_groups"
                :workout-type="workout.workout_type"
                :workout-timer="workoutTimer"
                :audio-sprite-map="workout.audio_sprite_map"
                :hide-timer="workout.hide_timer"
                @complete="completeWorkout"
              />
            </template>

            <cta-row>
              <v-button
                v-if="!workoutSession"
                class="workout-block__cta-button"
                block
                :is-loading="isLoading"
                @click="startBeepTest"
              >
                {{ $t("components.blocks.workoutBlock.startBeepTest") }}
              </v-button>
            </cta-row>
          </template>
        </template>

        <template v-else>
          <template v-if="isBuilderContext() && isConsideredEmpty">
            <div class="workout__empty text-muted">
              <v-icon class="mb-2" icon="fitness_center" size="2rem" />
              <div>
                {{ $t("components.blocks.workoutBlock.clickEditWorkout") }}
              </div>
            </div>
          </template>
          <template v-else>
            <template
              v-if="isFollowAlongSession && workoutSession && activeGroup"
            >
              <follow-along-session
                :workout-id="workout.id"
                :session="workoutSession"
                :timer-progress="timerProgress"
                :active-round="activeRound"
                :active-group="activeGroup"
                :active-set="activeSet"
                :next-group="nextGroup"
                :active-item="activeGroupItem"
                :progress="followAlongProgress"
                @captureClicked="showPartialCaptureForm"
              />
            </template>

            <workout-group
              v-for="group in groups"
              :key="group.id"
              :form-id="captureFormId"
              :workout="workout"
              :group="group"
              :ref="refKey(group)"
              @captureClicked="showPartialCaptureForm"
            />
          </template>

          <cta-row white-bg classes="workout__cta-row">
            <template v-if="!isModeChosen">
              <div class="text-left w-100 p-1">
                <h4 class="mt-0 mb-1">
                  {{
                    $t("components.blocks.workoutBlock.workoutModeChooseTitle")
                  }}
                </h4>
                <div class="mb-2">
                  <small class="text-grey-60">
                    {{
                      $t("components.blocks.workoutBlock.workoutModeChooseText")
                    }}
                  </small>
                </div>
                <button-group>
                  <v-button
                    :secondary="
                      workoutModePreference !== WORKOUT_SESSION_MODES.GROUPED
                    "
                    @click="chooseMode(WORKOUT_SESSION_MODES.GROUPED)"
                  >
                    {{ $t("components.blocks.workoutBlock.workoutList") }}
                  </v-button>
                  <v-button
                    :secondary="
                      workoutModePreference !==
                        WORKOUT_SESSION_MODES.FOLLOW_ALONG
                    "
                    @click="chooseMode(WORKOUT_SESSION_MODES.FOLLOW_ALONG)"
                  >
                    {{ $t("components.blocks.workoutBlock.followAlong") }}
                  </v-button>
                </button-group>
              </div>
            </template>

            <template v-if="isModeChosen">
              <div
                v-if="(hasStarted && workoutTimer) || hasActiveGroupWithTimer"
                class="w-100 mb-1 workout-block__timer"
              >
                <workout-timer
                  v-if="hasStarted && workoutTimer"
                  :class="[
                    'workout-block__item-timer',
                    hasActiveGroupWithTimer ? 'border-bottom' : null
                  ]"
                  :timer="workoutTimer"
                />

                <workout-timer
                  v-if="hasActiveGroupWithTimer"
                  x-small
                  :class="[
                    'd-flex align-items-center workout-block__group-timer',
                    { 'opacity-0': timersAreCountingIn }
                  ]"
                  :timer="groupTimer"
                  :hide-pause-control="hasItemTimer"
                  :hide-count-down="hasItemTimer"
                >
                  <template v-slot:before>
                    <b v-if="activeGroup" class="mr-auto">
                      {{ activeGroup.title }}
                    </b>
                  </template>
                </workout-timer>
              </div>

              <div class="d-flex w-100">
                <template v-if="hasStarted">
                  <template v-if="isFollowAlongSession">
                    <v-button
                      secondary
                      class="workout-block__cta-button --with-icon"
                      @click="handlePrevClick"
                    >
                      <v-icon class="text-muted" icon="west" />
                    </v-button>
                  </template>
                  <template v-if="isPresent(next)">
                    <v-button
                      primary
                      class="workout-block__cta-button --next flex-grow-1"
                      :is-loading="isLoading"
                      data-cy="workout-block__go-next"
                      @click="handleNextClick"
                    >
                      <div class="workout-block__cta-button-inner">
                        <div class="workout-block__cta-next-pretext text-small">
                          {{ $t("components.blocks.workoutBlock.updateNext") }}
                          <template v-if="isFollowAlongSession">
                            <template v-if="next.group === activeGroup">
                              <b v-if="activeItemHasMoreSets">
                                {{
                                  $t(
                                    "components.blocks.workoutBlock.setActiveGroup",
                                    {
                                      activeSet: activeSet + 1,
                                      activeGroup: activeGroupItem.sets
                                    }
                                  )
                                }}
                              </b>

                              <template v-if="groupHasMoreRounds">
                                <template v-if="activeItemHasMoreSets">
                                  {{ $t("components.blocks.workoutBlock.of") }}
                                </template>

                                <b>
                                  {{
                                    $t("components.blocks.workoutBlock.round")
                                  }}
                                  {{ activeRound + (next.isNextRound ? 1 : 0) }}
                                </b>
                              </template>
                            </template>
                            <template v-else-if="!next.group.is_rest">
                              {{ next.group.title }}
                            </template>
                          </template>
                          <template v-else-if="!isFollowAlongSession">
                            <template v-if="groupHasMoreRounds">
                              <b>
                                {{ $t("components.blocks.workoutBlock.round") }}
                                {{ activeRound + 1 }}
                              </b>
                            </template>
                          </template>
                        </div>
                        <b class="workout-block__cta-next-text text-small">
                          {{ nextTitle }}
                        </b>
                      </div>
                      <v-icon class="ml-auto text-muted" icon="east" />
                    </v-button>
                    <v-button
                      v-if="
                        (activeGroup && activeGroup.is_amrap) ||
                          (isFollowAlongSession &&
                            (nextGroup || activeGroupNextItem))
                      "
                      secondary
                      class="workout-block__cta-button --with-icon --skip"
                      data-cy="workout-block__skip"
                      @click="handleSkipClick"
                    >
                      <v-icon class="ml-auto text-muted" icon="skip_next" />
                    </v-button>
                  </template>
                  <template v-else>
                    <v-button
                      primary
                      class="workout-block__cta-button flex-grow-1"
                      data-cy="workout-block__finish"
                      :is-loading="isLoading"
                      @click="handleFinishClick"
                    >
                      {{ $t("components.blocks.workoutBlock.finishWorkout") }}
                    </v-button>
                  </template>
                </template>
                <template v-else>
                  <v-button
                    data-cy="choose-workout-mode"
                    class="workout-block__cta-button --with-icon"
                    secondary
                    @click="showModeChoser"
                  >
                    <template v-if="isFollowAlongSession">
                      <v-icon icon="repeat" size="1.5rem" />
                    </template>
                    <template v-else>
                      <v-icon icon="format_list_bulleted" size="1.5rem" />
                    </template>
                  </v-button>
                  <v-button
                    primary
                    class="workout-block__cta-button flex-grow-1"
                    data-cy="workout-block__start"
                    :is-loading="isLoading"
                    @click="() => startWorkout()"
                  >
                    {{ $t("components.blocks.workoutBlock.startWorkout") }}
                  </v-button>
                </template>
              </div>
            </template>
          </cta-row>
        </template>
      </div>
    </transition>

    <sign-up-prompt
      v-model="showSignUpConfirm"
      @confirm="() => triggerAuthModal()"
      @dismiss="() => completeWorkout()"
    />

    <alert-dialog
      :title="$t('components.blocks.workoutBlock.dialogLeaveWorkoutTitle')"
      :actions="[
        {
          title: $t('components.blocks.workoutBlock.dialogConfirmLeaveNo'),
          callback: closeExitConfirm
        },
        {
          title: $t(
            'components.blocks.workoutBlock.dialogConfirmLeaveWorkoutYes'
          ),
          callback: onExitConfirm
        }
      ]"
      :show="showExitConfirm"
      @close="closeExitConfirm"
    >
      {{
        $t("components.blocks.workoutBlock.dialogConfirmLeaveWorkoutContent")
      }}
    </alert-dialog>

    <alert-dialog
      :title="
        $t('components.blocks.workoutBlock.dialogLeaveWorkoutSessionTitle')
      "
      :actions="[
        {
          title: $t('components.blocks.workoutBlock.dialogConfirmLeaveNo'),
          callback: () => (showFollowAlongExit = false)
        },
        {
          title: $t(
            'components.blocks.workoutBlock.dialogConfirmLeaveWorkoutSessionYes'
          ),
          callback: onSessionExitConfirm
        }
      ]"
      :show="showFollowAlongExit"
      @close="() => (showFollowAlongExit = false)"
    >
      {{
        $t(
          "components.blocks.workoutBlock.dialogConfirmLeaveWorkoutSessionContent"
        )
      }}
    </alert-dialog>

    <alert-dialog
      :title="$t('components.blocks.workoutBlock.dialogSaveWorkoutTitle')"
      :actions="[
        {
          title: $t(
            'components.blocks.workoutBlock.dialogConfirmSaveWorkoutNo'
          ),
          callback: goBack
        },
        {
          title: $t(
            'components.blocks.workoutBlock.dialogConfirmSaveWorkoutYes'
          ),
          callback: onSaveConfirm
        }
      ]"
      :show="showSaveConfirm"
      @close="closeSaveConfirm"
    >
      {{ $t("components.blocks.workoutBlock.dialogConfirmSaveWorkoutContent") }}
    </alert-dialog>

    <v-modal short>
      <template :slot="WORKOUT_COMPLETE_HASH">
        <div class="p-3 pt-5 mt-3 container text-center">
          <v-icon icon="done_all" size="3rem" color="var(--brand-color-hex)" />
          <h2 class="mb-0">
            {{ $t("components.blocks.workoutBlock.workoutCompleteTitle") }}
          </h2>
          <p class="text-grey-70 mt-2">
            {{ $t("components.blocks.workoutBlock.workoutCompleteContent") }}
          </p>

          <cta-row white-bg overlay-modal row>
            <v-button
              round
              secondary
              class="mr-1"
              data-cy="workout-complete-modal-close"
              @click="() => $router.removeHash()"
            >
              <v-icon icon="close" />
            </v-button>
            <v-button
              block
              data-cy="workout-complete-modal-review"
              @click="onReviewResults"
            >
              {{ $t("components.blocks.workoutBlock.reviewResults") }}
            </v-button>
          </cta-row>
        </div>
      </template>
    </v-modal>

    <new-form-submission-modal
      v-if="captureFormId && workout.title"
      :form-id="captureFormId"
      :title="workout.title"
      is-missable
      @close="() => $router.removeHash()"
    />

    <portal selector="#toolbar-submission-button-portal">
      <v-button
        v-if="captureFormId && hasStarted"
        class="v-toolbar__button --reduced toolbar-submissions-button"
        secondary
        ghost
        round
        data-cy="new-submission-button"
        @click="() => handleFormCaptureClick()"
      >
        <v-icon icon="add_chart" />
      </v-button>
    </portal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from "vuex"

import { WORKOUT_TYPES, WORKOUT_SESSION_MODES } from "@/store/workouts"
import { ACTIONS } from "@/store/page-action-panel"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent } from "@/lib/utils"
import isBuilderContext from "@/lib/is-platform/is-builder-context"
import * as localStorage from "@/lib/local-storage"

import Timer, {
  TIMER_EVENTS,
  TIMER_STATUS,
  initializeAudio,
  COUNT_DIRECTIONS,
  COUNT_IN_DURATION
} from "@/lib/timer"
import { toPadding } from "@shared/block-ratio"
import { periodIntervalToSeconds } from "@/lib/time-utils"
import { getGroupTime, getGroupItemTime, UNIT_TYPES } from "@/lib/workout-utils"
import ScreenWake from "@/lib/screen-wake"

import CtaRow from "@/components/CtaRow"
import WorkoutTimer from "@/components/workout/Timer"
import SignUpPrompt from "@/components/workout/SignUpPrompt"
import NewFormSubmissionModal, {
  NEW_FORM_SUBMISSION_MODAL_HASH
} from "@/components/modals/NewFormSubmissionModal"

const ACTIVE_GROUP_INITIAL_INDEX = -1

const WORKOUT_COMPLETE_HASH = "#complete"

const ACTIVITY_TYPES = {
  GROUP_STARTED: "workout_group_started",
  GROUP_COMPLETED: "workout_group_completed",
  GROUP_ITEM_STARTED: "workout_group_item_started",
  GROUP_ITEM_COMPLETED: "workout_group_item_completed"
}

const NUMERIC_STATE = {
  activeGroupIndex: ACTIVE_GROUP_INITIAL_INDEX,
  activeItemIndex: 0,
  activeRound: 1,
  activeSet: 1
}

const screenWake = new ScreenWake()

export default {
  components: {
    CtaRow,
    WorkoutTimer,
    WorkoutGroup: () => import("@/components/workout/Group"),
    WorkoutSession: () => import("@/components/workout/Session"),
    AlertDialog: () => import("@/components/AlertDialog"),
    ButtonGroup: () => import("@/components/ButtonGroup"),
    FollowAlongSession: () => import("@/components/workout/FollowAlongSession"),
    SignUpPrompt,
    NewFormSubmissionModal
  },

  props: {
    workout: { type: Object, required: true },
    pageId: { type: Number, required: true },
    blockAction: { type: Object, default: null },
    spaceXRatio: {
      type: Number,
      default: 0
    },
    spaceTopRatio: {
      type: Number,
      default: 0
    },
    spaceBottomRatio: {
      type: Number,
      default: 0
    },
    invertColors: { type: Boolean, default: false }
  },

  data() {
    return {
      ...NUMERIC_STATE,

      workoutSession: null,
      isLoading: false,

      hasStarted: false,
      repeatTimer: false,

      showSignUpConfirm: false,

      showExitConfirm: false,
      showSaveConfirm: false,
      showSubmissionId: null,

      workoutTimer: null,
      groupTimer: null,

      shouldStartWorkout: false,
      shouldStartNextGroup: false,
      shouldTriggerAction: false,
      reviewingResults: false,

      WORKOUT_SESSION_MODES,
      WORKOUT_COMPLETE_HASH,

      isModeChosen:
        isBuilderContext() ||
        localStorage.get(localStorage.LS_KEYS.WORKOUT_MODE_PREFERENCE),

      showFollowAlongExit: false,
      showResultsCard: false
    }
  },

  mounted() {
    this.isLoading = true

    initializeAudio({
      spriteMap: this.workout.audio_sprite_map,
      callback: () => {
        this.isLoading = false
      },
      isMuted: !this.isBeepTest && this.areWorkoutsMuted
    })

    EventBus.$on(EVENTS.FORM_PROGRESS_CAPTURED, this.handleFormProgressCaptured)
    EventBus.$on(EVENTS.PAGE_EXIT_ATTEMPTED, this.handlePageExitAttempted)
  },

  deactivated() {
    this.completeWorkout()
  },

  beforeDestroy() {
    this.completeWorkout()

    EventBus.$off(
      EVENTS.FORM_PROGRESS_CAPTURED,
      this.handleFormProgressCaptured
    )
    EventBus.$off(EVENTS.PAGE_EXIT_ATTEMPTED, this.handlePageExitAttempted)

    this.resetFormSubmissionValues(this.captureFormId)

    this.removeAction({
      pageId: this.pageId,
      uid: ACTIONS.CAPTURE_WORKOUT
    })
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapState("workouts", ["workoutModePreference"]),
    ...mapGetters("forms", [
      "captureStatusesByUid",
      "firstCaptureEmptyIndex",
      "hasAnyCaptures"
    ]),
    ...mapGetters("workouts", ["areWorkoutsMuted"]),
    ...mapGetters("forms", ["formById"]),

    isConsideredEmpty() {
      return (
        !this.hasGroups ||
        (this.groups &&
          this.groups.length === 1 &&
          this.groups[0].items &&
          this.groups[0].items.length === 0)
      )
    },

    hasGroups() {
      return this.groups && this.groups.length > 0
    },

    isBeepTest() {
      return this.workout.workout_type === WORKOUT_TYPES.BEEP_TEST
    },

    blockPadding() {
      return toPadding(
        this.spaceTopRatio,
        this.spaceXRatio,
        this.spaceBottomRatio
      )
    },

    hasActiveGroupWithTimer() {
      return isPresent(this.activeGroup) && isPresent(this.groupTimer)
    },

    hasItemTimer() {
      return isPresent(this.workoutTimer)
    },

    groups() {
      return this.workout.workout_groups || []
    },

    nextGroup() {
      return this.groups[this.activeGroupIndex + 1]
    },

    prevGroup() {
      return this.groups[this.activeGroupIndex - 1]
    },

    activeGroup() {
      return this.groups[this.activeGroupIndex]
    },

    activeGroupItem() {
      return this.activeGroup.items[this.activeItemIndex]
    },

    activeItemIsRest() {
      return this.activeGroup && this.activeGroupItem?.is_rest
    },

    activeGroupNextItem() {
      return this.activeGroup.items[this.activeItemIndex + 1]
    },

    activeGroupPrevItem() {
      return this.activeGroup.items[this.activeItemIndex - 1]
    },

    activeGroupAndItemAndSet() {
      if (this.activeGroup) {
        return {
          group: this.activeGroup,
          item: this.activeGroupItem,
          groupIndex: this.activeGroupIndex,
          itemIndex: this.activeItemIndex,
          set: this.activeSet
        }
      } else {
        return {}
      }
    },

    activeGroupAndRound() {
      if (this.activeGroup) {
        return {
          group: this.activeGroup,
          round: this.activeRound
        }
      } else {
        return {}
      }
    },

    prev() {
      if (this.activeGroupItem && this.activeSet > 1) {
        return { item: this.activeGroupItem }
      }

      if (this.activeGroupPrevItem) {
        return { item: this.activeGroupPrevItem }
      }

      if (this.activeRound > 1) {
        const { items } = this.activeGroup

        if (items.length > 0) {
          return { item: items[items.length - 1] }
        } else {
          return { group: this.activeGroup }
        }
      }

      if (this.prevGroup) {
        const { items } = this.prevGroup
        if (items.length > 0) {
          return { item: items[items.length - 1] }
        } else {
          return { group: this.prevGroup }
        }
      }

      return {}
    },

    groupHasMoreRounds() {
      return (
        this.activeGroup &&
        (this.activeGroup.is_amrap ||
          (this.activeGroup.rounds &&
            this.activeRound < this.activeGroup.rounds))
      )
    },

    activeItemHasMoreSets() {
      return (this.activeGroupItem?.sets || 1) > this.activeSet
    },

    next() {
      if (!this.isFollowAlongSession) {
        if (this.groupHasMoreRounds) {
          return { group: this.activeGroup }
        } else {
          return this.nextGroup ? { group: this.nextGroup } : {}
        }
      }

      if (this.activeGroupItem && this.activeItemHasMoreSets) {
        return { group: this.activeGroup, item: this.activeGroupItem }
      }

      if (this.activeGroupNextItem) {
        return { group: this.activeGroup, item: this.activeGroupNextItem }
      }

      if (this.groupHasMoreRounds) {
        if (this.activeGroup.items[0]) {
          return {
            group: this.activeGroup,
            item: this.activeGroup.items[0],
            isNextRound: true
          }
        } else {
          return { group: this.activeGroup }
        }
      }

      if (this.nextGroup) {
        if (this.nextGroup.items[0]) {
          return { group: this.nextGroup, item: this.nextGroup.items[0] }
        } else {
          return { group: this.nextGroup }
        }
      }

      return {}
    },

    nextTitle() {
      const { item: nextItem, group: nextGroup } = this.next

      if (nextItem) {
        return nextItem.is_rest ? "Rest" : nextItem.exercise.title
      } else if (nextGroup) {
        return nextGroup.title
      } else {
        return null
      }
    },

    timerProgress() {
      if (this.activeGroup?.is_rest) {
        return this.groupTimer ? this.groupTimer.percentComplete / 100 : 0
      } else if (this.activeItemIsRest) {
        return this.workoutTimer ? this.workoutTimer.percentComplete / 100 : 0
      } else {
        return 0
      }
    },

    followAlongProgress() {
      const groupTotalSets = group =>
        group.items.reduce(
          (acc, item) => acc + (item.is_rest ? 1 : item.sets || 1),
          0
        )

      const totalPerGroup = this.groups.map(g =>
        g.is_amrap || g.is_rest ? 1 : (g.rounds || 1) * (groupTotalSets(g) || 1)
      )

      const totalCount = totalPerGroup.reduce((a, b) => a + b, 0)

      if (!totalCount) {
        return 0
      }

      let completedCount = totalPerGroup
        .filter((g, index) => index < this.activeGroupIndex)
        .reduce((a, b) => a + b, 0)

      if (!(this.activeGroup.is_amrap || this.activeGroup.is_rest)) {
        // TODOB: cypress for it all!!!
        completedCount +=
          (this.activeRound - 1) * (groupTotalSets(this.activeGroup) || 1) +
          (this.activeGroup.items || [])
            .filter((item, index) => index < this.activeItemIndex)
            .reduce(
              (acc, item) => acc + (item.is_rest ? 1 : item.sets || 1),
              0
            ) +
          this.activeSet -
          1
      }

      return (completedCount / totalCount) * 100
    },

    captureFormId() {
      return this.workout.capture_form_id
    },

    hasCaptureForm() {
      return !!this.captureFormId
    },

    showBeepTest() {
      return this.isBeepTest && this.workoutTimer && this.workoutSession
    },

    isFollowAlongSession() {
      return this.workoutModePreference === WORKOUT_SESSION_MODES.FOLLOW_ALONG
    },

    shouldPauseTimer() {
      return (
        this.showExitConfirm || this.showSignUpConfirm || this.showSaveConfirm
      )
    },

    timersAreCountingIn() {
      return (
        this.workoutTimer?.status === TIMER_STATUS.COUNTING_IN &&
        this.groupTimer?.status === TIMER_STATUS.COUNTING_IN
      )
    }
  },

  methods: {
    isPresent,
    isBuilderContext,

    ...mapMutations("forms", [
      "resetFormSubmissionValues",
      "increaseSubmissionsCount"
    ]),
    ...mapActions("forms", ["submitForm"]),
    ...mapActions("workouts", [
      "startSession",
      "completeSession",
      "recordSessionActivity"
    ]),
    ...mapMutations("workouts", ["setWorkoutModePreference"]),
    ...mapMutations("pageActionPanel", ["addAction", "removeAction"]),

    chooseMode(mode) {
      this.setWorkoutModePreference(mode)
      this.isModeChosen = true
    },

    showModeChoser() {
      if (!isBuilderContext()) {
        this.isModeChosen = false
      }
    },

    handleFormCaptureClick() {
      this.$router.push(NEW_FORM_SUBMISSION_MODAL_HASH)
    },

    handleNextClick() {
      if (this.isFollowAlongSession) {
        this.startFollowAlongNext()
      } else {
        this.startNextRoundOrGroup()
      }
    },

    handleFinishGroupTimer() {
      if (!this.workoutTimer) {
        if (this.groupHasMoreRounds || this.nextGroup) {
          if (this.activeGroup.is_amrap) {
            this.handleSkipClick()
          } else {
            this.startNextRoundOrGroup()
          }
        } else {
          this.completeWorkout(true)
        }
      }
    },

    handleTriggerAction() {
      if (this.blockAction) {
        EventBus.$emit(EVENTS.TRIGGER_BLOCK_ACTION, this.blockAction)
      }

      this.shouldTriggerAction = false
    },

    startFollowAlongNext() {
      if (this.activeGroupItem && this.activeItemHasMoreSets) {
        this.activeSet++
      } else if (this.activeGroupNextItem) {
        this.startNextItem()
        this.activeSet = 1
      } else if (this.groupHasMoreRounds) {
        this.goToGroupNextRound()
      } else if (this.nextGroup) {
        this.startNextGroup()
      } else {
        this.handleFinishClick()
      }
    },

    startNextRoundOrGroup() {
      if (this.groupHasMoreRounds) {
        this.goToGroupNextRound()
      } else if (this.nextGroup) {
        this.startNextGroup()
      }
    },

    goToGroupNextRound() {
      this.activeRound++
      this.activeItemIndex = 0
      this.activeSet = 1
    },

    handlePrevClick() {
      if (this.activeSet > 1) {
        this.activeSet--
      } else if (this.activeGroupPrevItem) {
        this.activeItemIndex--
        this.activeSet = 1
      } else if (this.activeRound > 1) {
        this.activeRound--
        this.activeItemIndex = Math.max(this.activeGroup.items.length - 1, 0)
        this.activeSet = 1
      } else if (this.prevGroup) {
        this.activeRound = this.prevGroup.rounds || 1
        this.activeItemIndex = Math.max(this.prevGroup.items.length - 1, 0)
        this.activeGroupIndex--
        this.activeSet = 1
      } else {
        this.showFollowAlongExit = true
      }
    },

    handleSkipClick() {
      if (this.nextGroup) {
        this.startNextGroup()
      } else {
        this.completeWorkout(true)
      }
    },

    onSessionExitConfirm() {
      this.completeWorkout()
      this.showFollowAlongExit = false
    },

    // start event bus handlers
    handleFormProgressCaptured(isPartialCapture) {
      if (isPartialCapture) {
        if (this.hasStarted) {
          this.shouldStartNextGroup =
            this.nextGroup &&
            this.activeGroup &&
            this.groupHasBeenCaptured(this.activeGroup)
        } else {
          this.shouldStartWorkout = true
        }
      }
    },

    handlePageExitAttempted({ pageId }) {
      if (pageId === this.pageId) {
        if (this.isFollowAlongSession) {
          if (this.workoutSession) {
            this.showFollowAlongExit = true
          } else {
            this.goBack()
          }
        } else if (this.hasStarted || this.hasAnyCaptures) {
          this.showExitConfirm = true
        } else {
          this.goBack()
        }
      }
    },
    // end event bus handlers

    closeExitConfirm() {
      this.showExitConfirm = false
    },

    closeSaveConfirm() {
      this.showSaveConfirm = false
    },

    goBack() {
      this.$router.back()
    },

    onExitConfirm() {
      this.closeExitConfirm()
      this.showSaveConfirm = true
    },

    onSaveConfirm() {
      this.closeSaveConfirm()

      if (this.isAuthenticated) {
        this.submitCaptureForm()
          .then(() => {
            this.$notify("Results have been saved")
          })
          .finally(() => {
            this.goBack()
          })
      } else {
        this.triggerAuthModal()
      }
    },

    onReviewResults() {
      if (!this.showSubmissionId) return

      this.$router.removeHash().then(() => {
        EventBus.$emit(EVENTS.SHOW_FORM_SUBMISSION, this.showSubmissionId)
        this.reviewingResults = true
        this.showSubmissionId = null
      })
    },

    goToGroup(group) {
      const groupComponent = this.$refs[this.refKey(group)]

      if (!groupComponent) return

      const groupEl = groupComponent[0].$el

      const toolbar = document.querySelector("#toolbar")
      const toolbarHeight = toolbar ? toolbar.offsetHeight : 0

      const page = document.querySelector(".v-page")

      this.activeGroupIndex++

      window.scrollTo({
        top:
          groupEl.getBoundingClientRect().top -
          page.getBoundingClientRect().top -
          toolbarHeight,
        left: 0,
        behavior: "smooth"
      })
    },

    startBeepTest() {
      this.workoutTimer = new Timer({ countIn: true })
      this.ensureWorkoutSessionStarted().then(() => {
        this.workoutTimer.start()
      })

      this.workoutTimer.on(TIMER_EVENTS.STARTED, () => {
        EventBus.$emit(EVENTS.WORKOUT_STARTED)
      })

      screenWake.keepAwake()
    },

    startWorkout(startTimer = true) {
      if (isBuilderContext()) {
        return
      }

      this.hasStarted = true

      if (this.hasGroups) {
        if (this.isFollowAlongSession) {
          this.activeGroupIndex = 0
          this.activeItemIndex = 0
        } else {
          this.goToGroup(this.groups[0], startTimer)
        }
      }

      this.ensureWorkoutSessionStarted()

      screenWake.keepAwake()
    },

    ensureWorkoutSessionStarted() {
      if (!this.workoutSession) {
        this.isLoading = true
        const activeGroup = this.activeGroup
        const activeGroupItem =
          this.isFollowAlongSession && !this.isBeepTest
            ? this.activeGroupItem
            : null

        return this.startSession({
          workoutId: this.workout.id,
          mode: this.workoutModePreference
        })
          .then(session => {
            this.workoutSession = session

            if (activeGroup) {
              this.recordActivity(
                activeGroup,
                ACTIVITY_TYPES.GROUP_STARTED
              ).then(() => {
                if (activeGroupItem) {
                  this.recordActivity(
                    activeGroup,
                    ACTIVITY_TYPES.GROUP_ITEM_STARTED,
                    activeGroupItem
                  )
                }
              })
            }
          })
          .finally(() => (this.isLoading = false))
      } else {
        return Promise.resolve()
      }
    },

    handleFinishClick() {
      if (this.isAuthenticated) {
        this.completeWorkout(true)
      } else {
        this.showSignUpConfirm = true
      }
    },

    startNextGroup() {
      const activeGroup = this.activeGroup
      if (activeGroup) {
        this.recordActivity(activeGroup, ACTIVITY_TYPES.GROUP_COMPLETED)
      }

      this.activeRound = 1
      this.activeSet = 1

      const nextGroup = this.nextGroup
      if (nextGroup) {
        if (this.isFollowAlongSession) {
          this.activeGroupIndex++
          this.activeItemIndex = 0
        } else {
          this.goToGroup(nextGroup)
        }

        this.recordActivity(nextGroup, ACTIVITY_TYPES.GROUP_STARTED)
      }

      this.shouldStartNextGroup = false
    },

    startPrevGroup() {
      const activeGroup = this.activeGroup
      if (activeGroup) {
        this.recordActivity(activeGroup, ACTIVITY_TYPES.GROUP_COMPLETED)
      }

      this.activeRound = 1
      this.activeSet = 1

      const prevGroup = this.prevGroup
      if (prevGroup) {
        this.activeGroupIndex--
        this.activeItemIndex = 0

        this.recordActivity(prevGroup, ACTIVITY_TYPES.GROUP_STARTED)
      }
    },

    startNextItem() {
      this.activeItemIndex++
    },

    createTimer(seconds, countIn = true) {
      const timer = new Timer({
        countIn: countIn,
        countDirection: COUNT_DIRECTIONS.DOWN,
        seconds: seconds
      })

      timer.on(TIMER_EVENTS.STARTED, () => {
        if (!this.hasStarted) {
          this.hasStarted = true
          this.activeGroupIndex = 0
        }
        this.ensureWorkoutSessionStarted()
      })

      return timer
    },

    initItemTimer(seconds, countIn = true) {
      this.workoutTimer = this.createTimer(seconds, countIn)

      this.workoutTimer.on(TIMER_EVENTS.FINISHED, () => {
        if (this.isFollowAlongSession) {
          if (this.repeatTimer) {
            this.repeatTimer = false
            this.initItemTimer(seconds, countIn)
            this.workoutTimer.start()
          } else {
            this.startFollowAlongNext()
          }
        } else {
          this.workoutTimer.reset()
        }
      })

      this.workoutTimer.on(TIMER_EVENTS.STARTED, () => {
        if (this.groupTimer?.status === TIMER_STATUS.PAUSED) {
          this.groupTimer.start()
        }
      })

      this.workoutTimer.on(TIMER_EVENTS.PAUSED, () => {
        if (this.groupTimer) {
          this.groupTimer.pause()
        }
      })
    },

    destroyTimer() {
      if (this.workoutTimer) {
        this.workoutTimer.reset()
      }

      this.workoutTimer = null
    },

    initGroupTimer(seconds) {
      if (seconds > 0) {
        this.groupTimer = this.createTimer(seconds, true)

        this.groupTimer.on(TIMER_EVENTS.FINISHED, this.handleFinishGroupTimer)
        this.groupTimer.start()
      } else {
        // Prevent amrap from getting stuck at 00:00 when clicking next round at 00:00
        this.handleFinishGroupTimer()
      }
    },

    destroyGroupTimer() {
      if (this.groupTimer) {
        this.groupTimer.reset()
      }
      this.groupTimer = null
    },

    resetNumericState() {
      Object.keys(NUMERIC_STATE).forEach(key => {
        this[key] = NUMERIC_STATE[key]
      })
    },

    async completeWorkout(autoCaptureResults = false) {
      this.hasStarted = false

      if (this.workoutTimer) {
        this.destroyTimer()
      }

      if (this.workoutSession) {
        this.isLoading = true

        if (this.activeGroup) {
          this.recordActivity(this.activeGroup, ACTIVITY_TYPES.GROUP_COMPLETED)
        }

        try {
          await this.completeSession({
            workoutId: this.workout.id,
            sessionId: this.workoutSession.id
          })

          if (autoCaptureResults) {
            const res = await this.submitCaptureForm()

            const { submission_id } = res.data
            this.showSubmissionId = submission_id

            this.$router.push(WORKOUT_COMPLETE_HASH)
            this.shouldTriggerAction = true
          }
        } finally {
          this.isLoading = false
        }

        this.workoutSession = null
      }

      this.resetNumericState()

      screenWake.release()
    },

    refKey(group) {
      return `workout-group-${group.id}`
    },

    submitCaptureForm() {
      return this.submitForm({
        formId: this.captureFormId,
        trigger: "workout_completed"
      }).then(res => {
        this.resetFormSubmissionValues(this.captureFormId)

        return res
      })
    },

    startTimer({ period, interval, countIn = true }) {
      if (isBuilderContext()) {
        return
      }

      const seconds = periodIntervalToSeconds(period, interval)

      if (this.workoutTimer) {
        this.workoutTimer.reset()
      }

      this.initItemTimer(seconds, countIn)

      this.workoutTimer.start()

      if (this.groupTimer?.status === TIMER_STATUS.PAUSED) {
        this.groupTimer.start()
      }
    },

    showPartialCaptureForm({ groupUid, itemUid, startIndex }) {
      EventBus.$emit(EVENTS.FORM_CAPTURE_PROGRESS, {
        formId: this.workout.capture_form_id,
        groupUid,
        itemUid,
        startIndex
      })
    },

    showCaptureForm() {
      if (isBuilderContext()) {
        return
      }

      if (this.isAuthenticated) {
        this.$router.push(NEW_FORM_SUBMISSION_MODAL_HASH)
      } else {
        this.triggerAuthModal()
      }
    },

    triggerAuthModal() {
      EventBus.$once(EVENTS.POST_AUTH_FLOW, this.handleAuthComplete)
      EventBus.$once(EVENTS.AUTH_ABORTED, () => {
        EventBus.$off(EVENTS.POST_AUTH_FLOW, this.handleAuthComplete)
      })
      this.$router.push("#auth")
    },

    handleAuthComplete() {},

    recordActivity(group, activityType, item) {
      return this.recordSessionActivity({
        workoutId: this.workout.id,
        sessionId: this.workoutSession.id,
        groupId: group.id,
        activityType,
        itemId: item?.id
      })
    },

    groupHasBeenCaptured(group) {
      if (group.is_amrap) {
        const statuses = this.captureStatusesByUid(group.uid)
        return statuses && statuses.length == 1 && statuses.every(s => !!s)
      } else {
        const rounds = group.rounds
        return group.items
          .filter(i => !i.is_rest)
          .every(item => {
            const statuses = this.captureStatusesByUid(item.uid)
            return (
              statuses && statuses.length == rounds && statuses.every(s => !!s)
            )
          })
      }
    },

    pauseTimersByStatus() {
      if (this.workoutTimer) {
        switch (this.workoutTimer.status) {
          case TIMER_STATUS.COUNTING_IN:
            return this.workoutTimer.reset()
          case TIMER_STATUS.RUNNING:
            return this.workoutTimer.pause()
        }
      }

      if (this.groupTimer) {
        switch (this.groupTimer.status) {
          case TIMER_STATUS.COUNTING_IN:
            return this.groupTimer.reset()
          case TIMER_STATUS.RUNNING:
            return this.groupTimer.pause()
        }
      }
    }
  },

  watch: {
    shouldPauseTimer(shouldPauseTimer) {
      if (shouldPauseTimer) {
        this.pauseTimersByStatus()
      }
    },

    activeGroupAndRound(
      { group, round },
      { group: prevGroup, round: prevRound }
    ) {
      const prevGroupTimer = this.groupTimer

      this.destroyGroupTimer()

      if (group && (group !== prevGroup || round !== prevRound)) {
        const { period, interval } = getGroupTime(group) || {}
        const isSameGroup = !!prevGroup && group.id == prevGroup.id

        if (period && interval) {
          const keepSecondsLeft =
            !!this.activeGroup?.is_amrap &&
            isSameGroup &&
            isPresent(prevGroupTimer)

          const countedSeconds = isPresent(prevGroupTimer)
            ? prevGroupTimer.totalSeconds - prevGroupTimer.secondsLeft
            : 0

          this.initGroupTimer(
            periodIntervalToSeconds(
              keepSecondsLeft
                ? prevGroupTimer.secondsLeft -
                    Math.max(COUNT_IN_DURATION - countedSeconds, 0)
                : period,
              keepSecondsLeft ? "sec" : interval
            )
          )
        }
      }
    },

    activeGroupAndItemAndSet(
      { group, item, groupIndex, itemIndex, set },
      {
        group: prevGroup,
        item: prevItem,
        groupIndex: prevGroupIndex,
        itemIndex: prevItemIndex,
        set: prevSet
      }
    ) {
      if (!this.isFollowAlongSession) {
        return
      }

      let itemTime = item ? getGroupItemTime(item) : null

      if (itemTime) {
        this.startTimer(itemTime)
      } else {
        this.destroyTimer()
      }

      this.repeatTimer =
        item &&
        (item !== prevItem || set !== prevSet) &&
        item.unit_type === UNIT_TYPES.SEC_SIDE

      const isGoingForward =
        [groupIndex, prevGroupIndex, itemIndex, prevItemIndex].every(
          t => t === 0 || t
        ) &&
        (groupIndex > prevGroupIndex || itemIndex > prevItemIndex)

      if (this.workoutSession && isGoingForward && item !== prevItem) {
        if (prevGroup && prevItem) {
          this.recordActivity(
            prevGroup,
            ACTIVITY_TYPES.GROUP_ITEM_COMPLETED,
            prevItem
          )
        }

        if (group && item) {
          this.recordActivity(group, ACTIVITY_TYPES.GROUP_ITEM_STARTED, item)
        }
      }
    },

    "$route.hash": {
      immediate: true,
      handler: function(hash, lastHash) {
        this.$nextTick(() => {
          const FORM_HASH = "#form"
          const SUBMISSION_HASH = "#form-submissions"
          const noHash = !hash || hash === "#"

          if (hash === FORM_HASH) {
            this.pauseTimersByStatus()
          }

          // only after form is closed
          if (noHash && lastHash === FORM_HASH) {
            if (this.shouldStartNextGroup && this.nextGroup) {
              this.startNextGroup()
              this.shouldStartNextGroup = false
            } else if (!this.hasStarted && this.shouldStartWorkout) {
              this.startWorkout(false)
              this.shouldStartWorkout = false
            }
          }

          if (lastHash === SUBMISSION_HASH && !hash.includes(SUBMISSION_HASH)) {
            this.reviewingResults = false
          }

          if (noHash && this.shouldTriggerAction && !this.reviewingResults) {
            this.handleTriggerAction()
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
.workout-block {
  &.--show-beep-test {
    position: fixed;
    top: 0;
    width: 100%;
    height: 100%;
    left: auto;
    right: auto;
    padding-top: $size--2 !important;
    background: $color-ui--white;
    z-index: 10;
  }

  &.--invert-colors {
    color: $color-ui--white !important;

    .workout-group-header__detail {
      color: $color-ui--white;
      .v-icon {
        opacity: 1;
      }

      p {
        color: $color-ui--white;
      }
    }
  }

  &.--timer-shown {
    .follow-along-session__content {
      padding-bottom: 16rem;
    }
  }

  &.--is-resting {
    .follow-along-session__content {
      padding-bottom: 13rem;
    }
  }
}

.workout-block__timer {
  background-color: $color-ui--grey-20;
  border-radius: var(--input-border-radius);
}

.workout-block__cta-button {
  margin-right: $size--1;
  transition: height 0s;

  &:last-child {
    margin-right: 0;
  }

  &.--next {
    text-align: left;
    display: flex;
    align-items: center;
    padding: $size--2 $size--4;

    .workout-block__cta-next-pretext {
      font-size: $font-size--x-small;
      line-height: 1.2;
      text-transform: uppercase;
    }

    .workout-block__cta-button-inner {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.--with-icon {
    flex-grow: 0 !important;
    flex-shrink: 0;
    padding: $size--2;
    color: $color-ui--grey-80;
    width: 54px; // easiest way to make square
    height: 54px;

    svg {
      width: 22px;
    }

    &.--skip {
      font-size: 1.5rem;
    }
  }

  &.--active {
    background-color: $color-ui--grey-80 !important;
    color: $color-ui--white;
  }
}

.workout__follow-icon {
  width: $size--9;
  margin-bottom: -$size--1;

  g {
    stroke: $color-ui--grey-40;
  }

  &.--active {
    g {
      stroke: var(--brand-color-hex);
    }
  }
}

.workout__empty {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.workout__cta-row {
  z-index: $modal--z-index - 1;
}
</style>
